import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useStudent } from "../../context/StudentContext";
import { api, ApiError, type TopicNoteReaderResponse } from "../../lib/api";
import { mapApiError } from "../../components/ui/StatusMessage";
import {
  ShortNotesReader,
  ShortNotesReaderSkeleton,
  ShortNotesReaderError,
  ShortNotesReaderEmpty,
} from "../../components/student/ShortNotesReader";

export function ShortNotesReaderPage() {
  const { topicId, subject } = useParams<{ topicId: string; subject?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { tokens } = useAuth();
  const { openUpgrade } = useStudent();
  const token = tokens?.accessToken ?? null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TopicNoteReaderResponse | null>(null);

  // Scroll to top on every navigation
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [topicId]);

  const loadTopicNotes = useCallback(async () => {
    if (!token || !topicId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.student.modules.shortNote(token, topicId);
      setData(res);
    } catch (err) {
      const msg = err instanceof ApiError ? mapApiError(err).message : "We could not load these notes. Please try again.";
      setError(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, topicId]);

  useEffect(() => {
    loadTopicNotes();
  }, [loadTopicNotes]);

  const currentSubjectSlug = subject || data?.course?.subject || "general";
  const backToModuleUrl = data?.module?.id
    ? `/student/study/${encodeURIComponent(currentSubjectSlug)}/modules/${data.module.id}`
    : `/student/study/${encodeURIComponent(currentSubjectSlug)}/modules`;

  const handleNavigate = (targetTopicId: string) => {
    if (subject) {
      navigate(`/student/study/${encodeURIComponent(subject)}/notes/${targetTopicId}`);
    } else {
      navigate(`/student/notes/${targetTopicId}`);
    }
  };

  const handleFinish = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate(backToModuleUrl);
    }
  };

  if (loading) {
    return (
      <div className="w-full text-foreground px-2 py-4 sm:py-6">
        <ShortNotesReaderSkeleton />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="w-full text-foreground px-2 py-4 sm:py-6">
        <ShortNotesReaderError
          error={error || "Topic not found"}
          onRetry={loadTopicNotes}
          backUrl={backToModuleUrl}
        />
      </div>
    );
  }

  if (!data.topic || (!data.topic.notes && !data.isLocked && !data.requiresPremium && data.access !== "locked")) {
    return (
      <div className="w-full text-foreground px-2 py-4 sm:py-6">
        <ShortNotesReaderEmpty backUrl={backToModuleUrl} onUpgrade={openUpgrade} />
      </div>
    );
  }

  return (
    <div className="w-full text-foreground px-2 py-4 sm:py-6">
      <ShortNotesReader
        data={data}
        onNavigate={handleNavigate}
        onFinish={handleFinish}
        onUpgrade={openUpgrade}
        backUrl={backToModuleUrl}
      />
    </div>
  );
}
